import { useState, type ReactNode } from "react";
import { cn } from "@/lib/utils";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { ChevronLeft, ChevronRight } from "lucide-react";

const MONTH_NAMES = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const MONTH_NAMES_LONG = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

export interface MonthYearStepperProps {
  year: number;
  month: number; // 1-indexed
  onSelect: (year: number, month: number) => void;
  minYear?: number;
  longLabel?: boolean;
  className?: string;
  trailing?: ReactNode;
  /**
   * When true, renders without its own card chrome (border / rounded
   * background) so it integrates cleanly inside a parent toolbar shell
   * that already provides those visuals.
   */
  flat?: boolean;
}

export function MonthYearStepper({
  year,
  month,
  onSelect,
  minYear = 2022,
  longLabel = false,
  className,
  trailing,
  flat = false,
}: MonthYearStepperProps) {
  const [pickerYear, setPickerYear] = useState(year);
  const [open, setOpen] = useState(false);

  const currentYear = new Date().getFullYear();
  const currentMonth = new Date().getMonth() + 1;
  const years = Array.from({ length: currentYear - minYear + 1 }, (_, i) => minYear + i).reverse();

  const canGoNext = year < currentYear || (year === currentYear && month < currentMonth);
  const canGoPrev = year > minYear || (year === minYear && month > 1);

  function goToPrev() {
    if (!canGoPrev) return;
    if (month === 1) onSelect(year - 1, 12);
    else onSelect(year, month - 1);
  }

  function goToNext() {
    if (!canGoNext) return;
    if (month === 12) onSelect(year + 1, 1);
    else onSelect(year, month + 1);
  }

  const label = `${(longLabel ? MONTH_NAMES_LONG : MONTH_NAMES)[month - 1]} ${year}`;

  return (
    <div
      className={cn(
        "inline-flex items-center h-9 text-sm",
        flat
          ? "bg-transparent"
          : "rounded-md border border-input bg-card",
        className
      )}
      data-testid="month-year-stepper"
    >
      <button
        type="button"
        disabled={!canGoPrev}
        onClick={goToPrev}
        className={cn(
          "h-full w-7 flex items-center justify-center hover:bg-accent disabled:opacity-40 disabled:cursor-not-allowed transition-colors",
          flat ? "" : "rounded-l-md"
        )}
        aria-label="Previous month"
        data-testid="month-year-stepper-prev"
      >
        <ChevronLeft className="h-4 w-4" />
      </button>

      <Popover
        open={open}
        onOpenChange={(o) => {
          if (o) setPickerYear(year);
          setOpen(o);
        }}
      >
        <PopoverTrigger asChild>
          <button
            type="button"
            className={cn(
              "h-full px-2.5 font-medium text-sm hover:bg-accent transition-colors min-w-[120px] text-center",
              flat ? "" : "border-x border-input"
            )}
            data-testid="month-year-stepper-label"
          >
            {label}
          </button>
        </PopoverTrigger>
        <PopoverContent className="filter-popout-glow w-[300px] p-0" align="center">
          <div className="flex">
            <div className="w-[90px] border-r max-h-[240px] overflow-y-auto py-1">
              <p className="px-3 py-1.5 text-xs font-medium text-muted-foreground">Year</p>
              {years.map((y) => (
                <button
                  key={y}
                  onClick={() => setPickerYear(y)}
                  className={cn(
                    "w-full text-left px-3 py-1.5 text-sm hover:bg-accent transition-colors",
                    pickerYear === y && "bg-primary/10 font-semibold"
                  )}
                >
                  {y}
                  {pickerYear === y && <ChevronRight className="h-3 w-3 inline ml-1 opacity-50" />}
                </button>
              ))}
            </div>
            <div className="flex-1 p-3">
              <p className="text-xs font-medium text-muted-foreground mb-2">Month</p>
              <div className="grid grid-cols-3 gap-1.5">
                {MONTH_NAMES.map((m, idx) => {
                  const monthNum = idx + 1;
                  const isSelected = pickerYear === year && monthNum === month;
                  const isFuture = pickerYear === currentYear && monthNum > currentMonth;
                  return (
                    <button
                      key={m}
                      disabled={isFuture}
                      onClick={() => {
                        onSelect(pickerYear, monthNum);
                        setOpen(false);
                      }}
                      className={cn(
                        "px-2 py-1.5 rounded text-sm transition-colors",
                        isSelected
                          ? "bg-primary/15 text-primary font-semibold"
                          : "hover:bg-accent",
                        isFuture && "opacity-40 cursor-not-allowed"
                      )}
                    >
                      {m}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        </PopoverContent>
      </Popover>

      <button
        type="button"
        disabled={!canGoNext}
        onClick={goToNext}
        className={cn(
          "h-full w-7 flex items-center justify-center hover:bg-accent disabled:opacity-40 disabled:cursor-not-allowed transition-colors",
          trailing || flat ? "" : "rounded-r-md"
        )}
        aria-label="Next month"
        data-testid="month-year-stepper-next"
      >
        <ChevronRight className="h-4 w-4" />
      </button>

      {trailing != null && (
        <div className="flex items-center pr-1.5 pl-0.5 border-l border-input h-full rounded-r-md">
          {trailing}
        </div>
      )}
    </div>
  );
}
